import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { supported, notSupportedReason } from '@mapbox/mapbox-gl-supported';

import clsx from '../clsx';
import MapContext from './context';
import mapStyles from './map-styles';
import { getBoundsFromLatLng } from './mapboxGeometry';
import { getAccessToken, languageMapper } from './utils';
import { POI_TYPES } from './constants';
import PointsOfInterest from './PointsOfInterest';
import styles from './Map.module.scss';
import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * This element will be displayed when mapbox-gl is not supported and when
 * `notSupportedFallbackElement` prop is not set on the Map component.
 */
const NotSupportedFallbackElementDefault = () => (
  <div className={styles.notSupported}>
    {/* TODO: update once we have a better specification on how we can pass in translated content into Map component */}
    <p>Browser does not support Mapbox GL</p>
  </div>
);

const Map = ({
  APIToken,
  latLng,
  options,
  children,
  className,
  controlPlacement,
  geolocateControl,
  shouldCenterOnGeolocate,
  compassControl,
  language,
  mapboxRef: setMapboxRefProp,
  zoomControl,
  onNotSupported,
  notSupportedFallbackElement,
  measurementUnits,
  poiOptions
}) => {
  const [isSupported, setIsSupported] = useState(true);
  const [map, setMap] = useState(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    if (map && map.getStyle() !== options.style) {
      map.setStyle(options.style);
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.style]);

  useEffect(() => {
    // Check to see if the browser supports Mapbox GL
    if (!supported()) {
      // `onNotSupported` is an optional callback method that can be defined if the
      // rendering application wants to handle Mapbox GL not being supported in some
      // way i.e. logging an error to Sentry some other service or rendering some sort
      // of fallback component
      if (onNotSupported) {
        const reason = notSupportedReason();
        const onNotSupportedMessage = `Mapbox GL is not supported: ${reason}`;
        onNotSupported(onNotSupportedMessage);
      }
      setIsSupported(false);

      return undefined;
    }

    mapboxgl.accessToken = getAccessToken(APIToken);

    // Only call setRTLTextPlugin when status is 'unavailable' (i.e. not requested or removed)
    // https://docs.mapbox.com/mapbox-gl-js/api/properties/#getrtltextpluginstatus
    if (mapboxgl.getRTLTextPluginStatus() === 'unavailable') {
      mapboxgl.setRTLTextPlugin(
        'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
        null,
        true // Lazy load the plugin, only loaded when the map first encounters Hebrew or Arabic text.
      );
    }

    const mapbox = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyles.standard,
      navigation: true,
      maxZoom: 21,
      fitBoundsOptions: { padding: 24 },
      pitchWithRotate: false,
      dragRotate: false,
      renderWorldCopies: false,
      performanceMetricsCollection: false,
      ...(latLng.length > 0 && { bounds: getBoundsFromLatLng(latLng) }),
      ...options
    });

    let geolocate;
    if (geolocateControl) {
      geolocate = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        fitBoundsOptions: {
          maxZoom: 13
        },
        showUserLocation: true,
        showAccuracyCircle: true
      });
      mapbox.addControl(geolocate, controlPlacement);
    }

    mapbox.addControl(
      new mapboxgl.ScaleControl({
        maxWidth: 100,
        unit: measurementUnits
      })
    );

    mapbox.addControl(
      new mapboxgl.NavigationControl({
        showCompass: compassControl,
        showZoom: zoomControl
      }),
      controlPlacement
    );

    // TODO: look into if there are better ways on doing this
    // 1) is this the best place to add this?
    // 2) should "language" be required prop form Map component?
    // 3) is there a way to make it easier to add controls to Map component?
    // 4) What if we want to add another MapboxLanguage for another source/layer,
    //    how will that work?
    // We need to add a language control for each source separately and the plugin
    // gracefully handles if the source does not exist in the eventually loaded
    // map.
    if (language) {
      const languageOptions = {
        defaultLanguage: languageMapper(language),
        supportedLanguages: [
          'en',
          'de',
          'es',
          'fr',
          'it',
          'ja',
          'ko',
          'nl',
          'pt',
          'ru',
          'zh',
          'zh-Hans',
          'zh-Hant-TW'
        ]
      };
      const poiLanguageControl = new MapboxLanguage({
        ...languageOptions,
        ...{ languageSource: 'pois' }
      });
      const streetsLanguageControl = new MapboxLanguage({
        ...languageOptions,
        ...{ languageSource: 'composite' }
      });
      mapbox.addControl(poiLanguageControl);
      mapbox.addControl(streetsLanguageControl);
    }

    mapbox.on('load', () => {
      setMap(mapbox);
      setMapboxRefProp(mapbox);
      if (geolocate && shouldCenterOnGeolocate) {
        geolocate.trigger();
      }
    });

    return () => {
      mapbox.remove();
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={mapContainerRef} className={clsx(styles.container, className)}>
      {map && (
        <MapContext.Provider value={{ map }}>
          <PointsOfInterest
            measurementUnits={measurementUnits}
            {...poiOptions}
          />
          {children}
        </MapContext.Provider>
      )}
      {!isSupported && notSupportedFallbackElement}
    </div>
  );
};

Map.propTypes = {
  // TODO: rename this prop to accessToken to match with mapbox-gl value once we updated
  // all the places where APIToken is set
  APIToken: PropTypes.string,
  latLng: PropTypes.arrayOf(PropTypes.array),
  children: PropTypes.node,
  options: PropTypes.shape(),
  mapboxRef: PropTypes.func,
  className: PropTypes.string,
  controlPlacement: PropTypes.oneOf([
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right'
  ]),
  geolocateControl: PropTypes.bool,
  shouldCenterOnGeolocate: PropTypes.bool,
  compassControl: PropTypes.bool,
  language: PropTypes.string,
  zoomControl: PropTypes.bool,
  onNotSupported: PropTypes.func,
  notSupportedFallbackElement: PropTypes.node,
  measurementUnits: PropTypes.string,
  poiOptions: PropTypes.shape({
    visible: PropTypes.bool,
    routeType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    poiTypes: PropTypes.arrayOf(
      PropTypes.oneOf([
        POI_TYPES.static,
        POI_TYPES.startPoint,
        POI_TYPES.bikeShare
      ])
    ),
    includeRouteCount: PropTypes.bool
  })
};

Map.defaultProps = {
  APIToken: null,
  children: null,
  latLng: [],
  options: {},
  mapboxRef: () => {},
  className: '',
  controlPlacement: 'top-left',
  geolocateControl: false,
  shouldCenterOnGeolocate: false,
  compassControl: false,
  language: null,
  zoomControl: true,
  onNotSupported: null,
  notSupportedFallbackElement: <NotSupportedFallbackElementDefault />,
  measurementUnits: 'metric',
  poiOptions: {}
};

export default Map;
